import { useState, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2,
  AlertTriangle, XCircle, Download, ArrowRight
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

export default function BulkImportPage() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    setFile(selected);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) { toast.error('Select a CSV file first'); return; }
    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/products/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} products`);
      } else {
        toast.error('No products were imported. Check errors below.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = 'name,generic_name,category,manufacturer,unit_price,unit_cost,stock_quantity,min_stock_level,preferred_supplier,requires_prescription,description,barcode';
    const sample = 'Aspirin 100mg,Acetylsalicylic Acid,Pain Relief,Bayer,3.50,1.80,200,30,PharmaCorp Ltd,false,Low dose aspirin,8901234567890';
    const csv = `${headers}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pharmxai-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="bulk-import-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Bulk Import Products
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Import products from a CSV file to quickly populate your inventory
        </p>
      </div>

      {/* Template Download */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">Download CSV Template</p>
              <p className="text-xs text-muted-foreground">Use this template to format your product data correctly</p>
            </div>
          </div>
          <Button variant="outline" onClick={downloadTemplate} className="gap-1.5 shrink-0" data-testid="download-template-btn">
            <Download className="h-4 w-4" /> Template
          </Button>
        </CardContent>
      </Card>

      {/* Required Columns */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">CSV Column Reference</CardTitle>
          <CardDescription>Required and optional columns for the import file</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 p-2 bg-primary/5 rounded">
              <Badge className="text-[10px]">Required</Badge>
              <span className="font-mono text-xs">name</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-primary/5 rounded">
              <Badge className="text-[10px]">Required</Badge>
              <span className="font-mono text-xs">unit_price</span>
            </div>
            {[
              { col: 'generic_name', hint: 'Generic/chemical name' },
              { col: 'category', hint: 'Default: General' },
              { col: 'manufacturer', hint: 'Brand manufacturer' },
              { col: 'unit_cost', hint: 'Cost/purchase price' },
              { col: 'stock_quantity', hint: 'Opening stock count' },
              { col: 'min_stock_level', hint: 'Reorder threshold (default: 10)' },
              { col: 'preferred_supplier', hint: 'Supplier name' },
              { col: 'requires_prescription', hint: 'true/false' },
              { col: 'description', hint: 'Product notes' },
              { col: 'barcode', hint: 'UPC/EAN barcode' },
            ].map(({ col, hint }) => (
              <div key={col} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                <Badge variant="outline" className="text-[10px]">Optional</Badge>
                <span className="font-mono text-xs">{col}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{hint}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card data-testid="upload-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" /> Upload CSV File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="drop-zone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                <Badge variant="secondary" className="text-xs">{(file.size / 1024).toFixed(1)} KB</Badge>
              </div>
            ) : (
              <div>
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Click to select a CSV file</p>
                <p className="text-xs text-muted-foreground mt-1">Maximum recommended: 1000 products per import</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="csv-file-input"
          />

          <Button
            className="w-full h-11 font-semibold gap-2"
            onClick={handleImport}
            disabled={!file || importing}
            data-testid="import-btn"
          >
            {importing ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
            ) : (
              <><Upload className="h-4 w-4" /> Import Products</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4 animate-fade-in-up" data-testid="import-results">
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4">
            <Card data-testid="result-imported">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-emerald-500" />
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{result.imported}</p>
                <p className="text-xs text-muted-foreground">Imported</p>
              </CardContent>
            </Card>
            <Card data-testid="result-stock-added">
              <CardContent className="p-4 text-center">
                <FileSpreadsheet className="h-6 w-6 mx-auto mb-1 text-primary" />
                <p className="text-2xl font-bold">{result.total_stock_added || 0}</p>
                <p className="text-xs text-muted-foreground">Stock Added</p>
              </CardContent>
            </Card>
            <Card data-testid="result-skipped">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-amber-500" />
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{result.skipped}</p>
                <p className="text-xs text-muted-foreground">Skipped</p>
              </CardContent>
            </Card>
            <Card data-testid="result-errors">
              <CardContent className="p-4 text-center">
                <XCircle className="h-6 w-6 mx-auto mb-1 text-rose-500" />
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{result.errors?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Errors</p>
              </CardContent>
            </Card>
          </div>

          {/* Auto-Created Entities */}
          {(result.suppliers_created > 0 || result.categories_created > 0 || result.manufacturers_created > 0 || result.generic_names_resolved > 0) && (
            <Card className="border-primary/30 bg-primary/5" data-testid="auto-created-banner">
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> Auto-created from import data:
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.suppliers_created > 0 && (
                    <Badge variant="outline" className="text-xs gap-1">{result.suppliers_created} new supplier{result.suppliers_created > 1 ? 's' : ''}</Badge>
                  )}
                  {result.categories_created > 0 && (
                    <Badge variant="outline" className="text-xs gap-1">{result.categories_created} new categor{result.categories_created > 1 ? 'ies' : 'y'}</Badge>
                  )}
                  {result.manufacturers_created > 0 && (
                    <Badge variant="outline" className="text-xs gap-1">{result.manufacturers_created} new manufacturer{result.manufacturers_created > 1 ? 's' : ''}</Badge>
                  )}
                  {result.generic_names_resolved > 0 && (
                    <Badge variant="outline" className="text-xs gap-1 border-primary/40 text-primary">{result.generic_names_resolved} generic name{result.generic_names_resolved > 1 ? 's' : ''} auto-detected by AI</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Details */}
          {result.errors?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Import Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-1.5">
                    {keyed(result.errors, err => `${err.row}-${err.error}`).map(({ item: err, key }) => (
                      <div key={key} className="flex items-start gap-2 text-sm py-1">
                        <Badge variant="outline" className="text-[10px] shrink-0">Row {err.row}</Badge>
                        <span className="text-muted-foreground">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {result.imported > 0 && (
            <div className="text-center">
              <Button variant="outline" onClick={() => window.location.href = '/inventory'} className="gap-1.5" data-testid="go-to-inventory-btn">
                View in Inventory <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
